import {_} from 'lodash';
import * as ko from "knockout";
import {ObjectTypeId, TEXT_WRAPPER_END, TEXT_WRAPPER_START} from "../../../../constants";
import {IRequestObjectMulti} from "../../../../req/index";
import {nsSyncFields} from "../../../../req/nsSyncFields";
import {ISyncField} from "../../../../index";
import {ITextRange} from "../../../dynamic-variable/index";
import {IMutableSyncfieldParameters, TState} from "./index";
import {nsForm} from "../../nsForm";
import {global} from "../../../../appContext";
import {KOSubscriptionTracker} from "../../../../util/KOSubscriptionTracker";
import AbstractText from "../../../dynamic-variable/AbstractText";
import pubsub from "../../../../pubsub";
import {getObjectDefinition, IObjectDefinition} from "../../../../util/misc";
import getParameters = nsForm.getParameters;

/**
 * Tracks the caret within the editor and updates it's properties with data about the current dynamic element. Also
 * supports updating the editor with changes to that element.
 */
export class DynamicProperties {
    private allSyncfields: ISyncField[] = [];
    private onEventHandler: () => void;
    private onKeyHandler!: () => void;
    private readonly isSilent: KnockoutObservable<boolean> = ko.observable(false);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly name: KnockoutObservable<string | null> = ko.observable(null);
    private readonly onFocusInHandler: (e: FocusEvent) => void;
    private requestAnimationFrameId!: number;
    private syncfieldRequests: IRequestObjectMulti | undefined;
    private textEditor: AbstractText | null = null;
    public readonly observables: KnockoutObservableArray<IMutableSyncfieldParameters> = ko.observableArray<IMutableSyncfieldParameters>([]);
    public readonly reason: KnockoutObservable<string | null> = ko.observable(null);
    public readonly state: KnockoutObservable<TState> = ko.observable<TState>('waiting');
    public readonly syncfield: KnockoutObservable<ISyncField | null> = ko.observable(null);
    public readonly textRange: KnockoutObservable<ITextRange | null> = ko.observable(null);

    private static EditorEvents = 'keyup keydown click paste cut input NodeChange';
    private static FocusEvents: string = 'focus focusin focusout';

    constructor() {
        this.kosub.subscribe(global.login.selectedOrganization, () => this.disengageEditor());
        this.kosub.subscribe(global.router.currentRoute, () => this.disengageEditor());
        this.onEventHandler = this.updateProperties.bind(this);
        this.onFocusInHandler = this.onFocusIn.bind(this);
        this.kosub.subscribeChanged(global.textEditor, (newEditor: AbstractText | null) => {
            if (newEditor?.dynamicSupport) {
                this.engageEditor();
            }
        })
        this.state('waiting');
    }

    private getAllObjectTypeIds = ko.pureComputed((): ObjectTypeId[] => {
        if (!this.textEditor) {
            return [];
        }
        return [this.textEditor.objectTypeId(), ...this.textEditor.objectTypeIds()];
    })

    private onFocusIn(e: FocusEvent) {
        const target: HTMLElement = (e.target ?? e.relatedTarget) as HTMLElement;
        if (!target) {
            return;
        }

        if (target?.closest('[dynamicsupport="true"], [role="complementary"], [role="dialog"]')) {
            return;
        }

        this.disengageEditor();
    }

    private engageEditor() {
        this.textEditor = global.textEditor();
        if (!this.textEditor) {
            return;
        }
        this.initListeners();
        this.initRequest();
    }

    private disengageEditor() {
        this.killListeners();
        this.state('waiting');
        this.textEditor = null;
        this.textRange(null);
        this.name(null);
        this.syncfield(null);
    }

    private initRequest() {
        const loaded = this.getAllObjectTypeIds().map(id => nsSyncFields.getStoreSyncfields(id));
        if (loaded.every(Boolean)) {
            this.allSyncfields = _.flattenDeep(loaded);
            this.updateProperties();
            return;
        }
        this.state('loading');
        this.syncfieldRequests?.promises.forEach(p => p.abort());
        this.syncfieldRequests = nsSyncFields.getSyncFieldsMulti(this.getAllObjectTypeIds());
        this.syncfieldRequests
            .request
            .then((data: ISyncField[]) => {
                this.allSyncfields.push(...data);
                this.updateProperties();
            })
            .catch((reason: string) => {
                this.reason(reason);
                this.state('error');
            })
    }

    private initListeners() {
        window.addEventListener('focusin', this.onFocusInHandler);
        pubsub.on(this, 'dynamic.editor.focusin', (je: JQuery.FocusEvent, e: FocusEvent) => this.onFocusIn(e));
        DynamicProperties.EditorEvents.split(' ').forEach(e => {
            this.textEditor?.addEventListener(e, this.onEventHandler);
        });
    }

    private killListeners() {
        window.removeEventListener('focusin', this.onFocusInHandler);
        pubsub.off(this);
        DynamicProperties.EditorEvents.split(' ').forEach(e => {
            this.textEditor?.removeEventListener(e, this.onEventHandler);
        });
    }

    protected updateProperties() {
        if (this.isSilent()) {
            return;
        }
        if (!this.textEditor) {
            return;
        }
        const textRange = this.textEditor.getTextRange();
        const name = textRange?.str.split(';')[0] ?? null;
        const regExpStr = '^\\[' + name + '[;|\\]]';
        let syncfield: ISyncField | null = null;
        if (textRange) {
            syncfield = this.allSyncfields?.find(sf => {
                const a = this.getAllObjectTypeIds().includes(sf.objecttypeid);
                const b = sf.name.toLowerCase() == textRange.str.toLowerCase();
                const c = RegExp(regExpStr, 'i').test(sf.name);
                return a && (b || c);
            }) ?? null;
        }

        const isEqual = ((): boolean => {
            if (!textRange || !name || !syncfield) {
                return false;
            }
            let textRangeA = _.clone(this.textRange());
            let textRangeB = _.clone(textRange);
            delete textRangeA?.caret;
            delete textRangeB.caret;
            const a = _.isEqual(textRangeA, textRangeB);
            const b = name == this.name();
            const c = _.isEqual(syncfield, this.syncfield());
            return a && b && c;
        })();

        // if there are changes we'll "turn off" the component so that it will reinitialize when it's displayed again
        if (!isEqual) {
            this.state('waiting');
        }

        this.textRange(textRange);
        this.name(name);
        this.syncfield(syncfield);

        if (!syncfield) {
            this.observables([]);
        } else {
            if (!isEqual) {
                const names = this.stripWrappers(syncfield.name).split(';');
                const displaynames = this.stripWrappers(syncfield.displayname).split(';');
                const parameters = syncfield.parameters?.split(';') ?? [];
                const values: string[] = getParameters(textRange!);
                const editor = this.textEditor!;
                this.observables(names.map((name: string, i: number): IMutableSyncfieldParameters => {
                    return {
                        displayname: displaynames[i] ?? '',
                        name: names[i] ?? '',
                        parameter: parameters[i] ?? `Parameter ${i}`,
                        val: ko.observable(values[i] ?? ''),
                        objectTypeId: editor.objectTypeId,
                        objectTypeIds: editor.objectTypeIds,
                    }
                }));
            }
        }

        if (!!textRange && !!name && !!syncfield) {
            this.state('ready');
        } else {
            this.state('waiting');
        }

    }

    private stripWrappers(str: string): string {
        if (str?.charAt(0) == TEXT_WRAPPER_START) {
            str = str.substring(1);
        }
        if (str?.charAt(str.length - 1) == TEXT_WRAPPER_END) {
            str = str.substring(0, str.length - 1);
        }
        return str ?? '';
    }

    private getObjDef(): IObjectDefinition | null {
        return getObjectDefinition(this.syncfield()?.objecttypeid);
    }

    getName(type: 'regular' | 'capitalize' | 'upper' | 'lower' = 'regular'): string {
        const name = this.name();
        const syncfield = this.syncfield();
        if (!name || !syncfield) {
            return '';
        }
        switch (type) {
            case "capitalize":
                return name.charAt(0).toUpperCase() + name.substring(1).toLowerCase();
            case "upper":
                return name.toUpperCase();
            case "lower":
                return name.toLowerCase();
            case "regular": {
                const ret = syncfield.name;
                try {
                    if (ret.charAt(0) == TEXT_WRAPPER_START) {
                        return /\[(.*?);*\]/.exec(ret)?.[1] ?? ret;
                    }
                } catch (err) {
                }
                return ret;
            }
        }
    }

    submit(): void {
        if (!this.textEditor) {
            return;
        }
        const str = this.observables().map(o => o.val()).join(';')
        this.textEditor.insertString(str);
        this.textRange(this.textEditor.getTextRange());
    }

    dispose() {
        this.killListeners();
        window.cancelAnimationFrame(this.requestAnimationFrameId);
        this.syncfieldRequests?.promises.forEach(p => p.abort());
        pubsub.off(this);
    }
}